import { create } from 'zustand';
import path from 'path-browserify';

export type UploadFile = {
  file: File;
  path: string;
  progress: number;
  speed: string;
  status: 'queued' | 'uploading' | 'completed' | 'error' | 'cancelled';
  error?: string;
};

type UploadStore = {
  files: Record<string, UploadFile>;
  controllers: Record<string, AbortController>;
  addFile: (file: File, path: string) => void;
  addFiles: (files: File[], basePath: string) => void;
  updateProgress: (fileKey: string, progress: number) => void;
  updateSpeed: (fileKey: string, speed: string) => void;
  updateStatus: (fileKey: string, status: UploadFile['status'], error?: string) => void;
  removeFile: (fileKey: string) => void;
  clearCompleted: () => void;
  clearAll: () => void;
  setController: (fileKey: string, controller: AbortController) => void;
  cancelUpload: (fileKey: string) => void;
  generateFileKey: (file: File, path: string) => string;
};

export const useUploadStore = create<UploadStore>((set, get) => ({
  files: {},
  controllers: {},
  
  generateFileKey: (file: File, path: string) => {
    return `${path}/${file.name}`;
  },
  
  addFile: (file: File, path: string) => {
    const fileKey = get().generateFileKey(file, path);
    set((state) => ({
      files: {
        ...state.files,
        [fileKey]: {
          file,
          path,
          progress: 0,
          speed: '',
          status: 'queued',
        },
      },
    }));
  },
  
  addFiles: (files: File[], basePath: string) => {
    const newFiles: Record<string, UploadFile> = {};
    files.forEach((file) => {
      const filePath = (file as any).path || '';
      
      const fullPath = filePath ? filePath : basePath;
      
      const fileKey = get().generateFileKey(file, fullPath);
      
      newFiles[fileKey] = {
        file,
        path: fullPath,
        progress: 0,
        speed: '',
        status: 'queued',
      };
    });
    
    set((state) => ({
      files: {
        ...state.files,
        ...newFiles,
      },
    }));
  },
  
  updateProgress: (fileKey: string, progress: number) => {
    set((state) => {
      const file = state.files[fileKey];
      if (!file) return state;
      
      return {
        files: {
          ...state.files,
          [fileKey]: {
            ...file,
            progress,
          },
        },
      };
    });
  },
  
  updateSpeed: (fileKey: string, speed: string) => {
    set((state) => {
      const file = state.files[fileKey];
      if (!file) return state;
      
      return {
        files: {
          ...state.files,
          [fileKey]: {
            ...file,
            speed,
          },
        },
      };
    });
  },
  
  updateStatus: (fileKey: string, status: UploadFile['status'], error?: string) => {
    set((state) => {
      const file = state.files[fileKey];
      if (!file) return state;
      
      return {
        files: {
          ...state.files,
          [fileKey]: {
            ...file,
            status,
            error,
          },
        },
      };
    });
  },
  
  removeFile: (fileKey: string) => {
    set((state) => {
      const { [fileKey]: _, ...restFiles } = state.files;
      const { [fileKey]: __, ...restControllers } = state.controllers;
      
      return {
        files: restFiles,
        controllers: restControllers,
      };
    });
  },
  
  clearCompleted: () => {
    set((state) => {
      const newFiles = { ...state.files };
      const newControllers = { ...state.controllers };
      
      Object.keys(newFiles).forEach((fileKey) => {
        if (newFiles[fileKey].status === 'completed') {
          delete newFiles[fileKey];
          delete newControllers[fileKey];
        }
      });
      
      return { 
        files: newFiles,
        controllers: newControllers
      };
    });
  },
  
  clearAll: () => {
    Object.values(get().controllers).forEach(controller => {
      controller.abort();
    });
    
    set({ files: {}, controllers: {} });
  },
  
  setController: (fileKey: string, controller: AbortController) => {
    set((state) => ({
      controllers: {
        ...state.controllers,
        [fileKey]: controller,
      },
    }));
  },
  
  cancelUpload: (fileKey: string) => {
    const controller = get().controllers[fileKey];
    if (controller) {
      controller.abort();
      
      get().updateStatus(fileKey, 'cancelled');
      
      set((state) => {
        const { [fileKey]: _, ...restControllers } = state.controllers;
        return { controllers: restControllers };
      });
    }
  },
})); 